import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";
import MuiDialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";

import { withTranslation } from "react-i18next";
import { isEmpty } from "lodash";

import fpItemService from "@plannel/services/fp/fp-item-service";
import FpGridSetting from "@plannel/pages/factory-plan/common/grid";
import dateUtils from "@plannel/utils/date-util";
import debug from "@plannel/utils/debug-util";

/**
 * 품목(Item) 조회/선택 전용 팝업 — old `ItemPopup.jsx`와 동일하게 선택 전용(관리 화면 없음,
 * 품목 자체 관리는 Item.js의 ItemTab에서 함). StagePopup.js와 동일 구성(더블클릭 선택,
 * 검색창/버튼 없음). 목록은 ItemTab이 쓰는 것과 같은 조회(`fpItemService.getItems`)를 재사용.
 *
 * props: open, onClose(), planScope, onSelect({itemCd, itemNm, itemClassCd})
 */
const ItemPopup = ({ t, open, onClose, planScope, onSelect }) => {
  const gridRef = useRef();
  const [rowData, setRowData] = useState([]);
  const gridOptions = useMemo(() => FpGridSetting({ title: t("FP_ITEM_SELECT"), viewName: "" }), [t]);

  const columnDefs = useMemo(() => ([
    { headerName: t("FP_ITEM_CD"), field: "itemCd", colId: "itemCd", width: 180, editable: false },
    { headerName: t("FP_ITEM_NM"), field: "itemNm", colId: "itemNm", width: 220, editable: false },
    // old lookupDisplay+lookupData(코드→다국어 라벨 표시, editable:false) 대응 — 앱 전역에서
    // 이미 쓰이는 valueFormatter:(params)=>t(params.value) 패턴(예: MpReview.js/RpReview.js)의
    // FP식 변형. FP 공통코드 콤보 라벨 키 컨벤션(`${grpCd}_${code}`, §8)을 그대로 재사용 —
    // 키가 없으면 원본 코드를 그대로 보여준다(§8 codeComboCellEditorParams와 동일 폴백).
    { headerName: t("FP_ITEM_CLASS_CD"), field: "itemClassCd", colId: "itemClassCd", width: 120, editable: false, type: ["centerAligned"],
      valueFormatter: (params) => {
        const key = `FP_ITEM_CLASS_CD_${params.value}`;
        const label = t(key);
        return label !== key ? label : params.value;
      } },
    {
      headerName: t("FP_COL_AUDIT"), groupId: "audit", marryChildren: true, openByDefault: false,
      children: [
        { headerName: t("updatedTs"), field: "modifyDttm", colId: "modifyDttm", type: ["nonEditableColumn", "centerAligned"], width: 150, valueFormatter: dateUtils.formatDateTime },
        { headerName: t("updatedBy"), field: "modifyUser", colId: "modifyUser", type: ["nonEditableColumn", "centerAligned"], width: 110, columnGroupShow: "open" },
        { headerName: t("createdTs"), field: "createDttm", colId: "createDttm", type: ["nonEditableColumn", "centerAligned"], width: 150, valueFormatter: dateUtils.formatDateTime, columnGroupShow: "open" },
        { headerName: t("createdBy"), field: "createUser", colId: "createUser", type: ["nonEditableColumn", "centerAligned"], width: 110, columnGroupShow: "open" }
      ]
    }
  ]), [t]);

  const loadItems = () => {
    if (!planScope) return;
    fpItemService.getItems({ planScope })
      .then(response => setRowData(isEmpty(response.data) ? [] : response.data))
      .catch(e => { debug(e); setRowData([]); });
  };

  useEffect(() => {
    if (!open || !planScope) return;
    loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, planScope]);

  const onRowDoubleClicked = (e) => {
    if (!e.data) return;
    onSelect({ itemCd: e.data.itemCd, itemNm: e.data.itemNm, itemClassCd: e.data.itemClassCd });
    onClose();
  };

  return (
    <MuiDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t("FP_ITEM_SELECT")}</DialogTitle>
      <DialogContent>
        <Box className="ag-theme-balham" sx={{ height: 440, width: "100%" }}>
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            columnDefs={columnDefs}
            onRowDoubleClicked={onRowDoubleClicked}
            {...gridOptions}
            rowSelection={{ mode: "singleRow", checkboxes: false, enableClickSelection: true }}
          />
        </Box>
      </DialogContent>
    </MuiDialog>
  );
};

export default withTranslation()(ItemPopup);
